import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useAuth } from '../../../contexts/AuthContext';
import {
  Report,
  User,
  Site,
  canUserViewReport,
  convertTimestamp,
  COLLECTIONS,
} from '../../../types/firebase';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
const isSkimmer = (r: Report) =>
  r.status === 'Skimmer Detected!' || r.status === 'Skimmer Detected';

export interface TrendPoint {
  name: string;
  healthy: number;
  critical: number;
}

export interface FeedData {
  reports: Report[];
  sites: Site[];
  totalReports: number;
  skimmerCount: number;
  noSkimmerCount: number;
  trendData: TrendPoint[];
  siteStatusMap: Record<string, 'healthy' | 'alert'>;
  loading: boolean;
}

export function useFeedData(): FeedData {
  const { userProfile } = useAuth();
  /** Org-visible reports (private/shared); public community reports are excluded from the feed. */
  const [allVisibleReports, setAllVisibleReports] = useState<Report[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userProfile) {
      setLoading(false);
      return;
    }

    const fetch = async () => {
      try {
        setLoading(true);
        const reportsRef = collection(db, COLLECTIONS.REPORTS);
        let reportsData: Report[] = [];

        if (userProfile.role === 'Clerk') {
          const q = query(
            reportsRef,
            where('user.masterOwnerID', '==', userProfile.masterOwnerID),
            where('user.ownerID', '==', userProfile.ownerID),
            orderBy('createdAt', 'desc')
          );
          const snap = await getDocs(q);
          reportsData = snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            createdAt: convertTimestamp(d.data().createdAt),
          })) as Report[];
        } else {
          const privateQ = query(
            reportsRef,
            where('privacy', '==', 'private'),
            where('user.masterOwnerID', '==', userProfile.masterOwnerID),
            orderBy('createdAt', 'desc')
          );
          const privateSnap = await getDocs(privateQ);
          reportsData = privateSnap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            createdAt: convertTimestamp(d.data().createdAt),
          })) as Report[];
        }

        const visible = reportsData.filter((r) =>
          canUserViewReport(r, userProfile as User)
        );
        setAllVisibleReports(visible);

        const sitesCol = collection(db, 'sites');
        const sitesQ = query(
          sitesCol,
          where('masterOwnerID', '==', userProfile.masterOwnerID)
        );
        const sitesSnap = await getDocs(sitesQ);
        let sitesData = sitesSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as Site[];
        if (
          userProfile.role === 'Clerk' &&
          userProfile.assignedSites?.length
        ) {
          sitesData = sitesData.filter((s) =>
            userProfile.assignedSites!.includes(s.id)
          );
        }
        setSites(sitesData);
      } catch (err) {
        console.error('Feed data fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [userProfile]);

  const metricsReports = allVisibleReports.filter((r) => r.privacy !== 'public');
  const reportsForFeedUi = metricsReports;

  const now = Date.now();
  const thirtyDaysAgo = now - THIRTY_DAYS_MS;
  const reports30 = metricsReports.filter(
    (r) => convertTimestamp(r.createdAt).getTime() >= thirtyDaysAgo
  );
  const totalReports = reports30.length;
  const skimmerCount = reports30.filter(isSkimmer).length;
  const noSkimmerCount = reports30.filter(
    (r) => r.status === 'No Skimmer Detected'
  ).length;

  const trendData: TrendPoint[] = (() => {
    const byDay: Record<string, { healthy: number; critical: number }> = {};
    for (let i = 29; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      d.setHours(0, 0, 0, 0);
      const key = d.toISOString().slice(0, 10);
      byDay[key] = { healthy: 0, critical: 0 };
    }
    reports30.forEach((r) => {
      const date = convertTimestamp(r.createdAt);
      date.setHours(0, 0, 0, 0);
      const key = date.toISOString().slice(0, 10);
      if (byDay[key]) {
        if (isSkimmer(r)) byDay[key].critical += 1;
        else byDay[key].healthy += 1;
      }
    });
    return Object.entries(byDay)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, v]) => ({
        name: new Date(name).toLocaleDateString('en-US', {
          day: '2-digit',
          month: 'short',
        }),
        healthy: v.healthy,
        critical: v.critical,
      }));
  })();

  const siteStatusMap: Record<string, 'healthy' | 'alert'> = {};
  sites.forEach((s) => {
    siteStatusMap[s.id] = 'healthy';
  });
  reports30.filter(isSkimmer).forEach((r) => {
    const siteId =
      (r as any).readerDetails?.siteId ?? (r as any).siteId ?? null;
    if (siteId && siteStatusMap[siteId] !== undefined) {
      siteStatusMap[siteId] = 'alert';
    }
    const siteName = (r as any).readerDetails?.siteName;
    if (siteName && !siteId) {
      const matched = sites.find(
        (s) => s.name?.toLowerCase() === String(siteName).toLowerCase()
      );
      if (matched) siteStatusMap[matched.id] = 'alert';
    }
  });

  return {
    reports: reportsForFeedUi,
    sites,
    totalReports,
    skimmerCount,
    noSkimmerCount,
    trendData,
    siteStatusMap,
    loading,
  };
}
